/**
 * BudgetBarChart.jsx
 * -------------------
 * A reusable, presentation-only bar chart component using Recharts.
 * 
 * Visualizes either weekly or monthly spending against:
 *   - a target budget line
 *   - an average spending line
 * 
 * ✅ Props:
 *   - view: "week" | "month"       --> Controls label formatting and budget calculation
 *   - data: Array of { start_date, spending } sorted by date (oldest to newest)
 *   - monthlyBudget: number        --> Weekly budget is derived as (12 × monthlyBudget) / 52
 * 
 * 🔍 Behavior:
 *   - Current period = last item in data[]
 *   - Current period bar is highlighted
 *   - Legend is manually injected to show Budget and Avg Spending lines
 * 
 * 📦 Assumes:
 *   - Backend cron jobs populate weekly_spending and monthly_spending with start_date + spending
 *   - No frontend aggregation or date slicing is needed
 */

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
  Cell,
} from 'recharts';
import { format, parseISO } from 'date-fns';

const BudgetBarChart = ({ data, view, budget }) => {
  if (!data || data.length === 0) return null;

  // Add label + mark last entry as current
  const chartData = data.map((entry, index) => {
    const date = parseISO(entry.start_date);
    const isCurrent = index === data.length - 1;
    return {
      ...entry,
      label: view === 'week' ? format(date, 'MMM d') : format(date, "MMM ''yy"),
      isCurrent,
    };
  });

  // Calculate average spending across the dataset
  const averageSpending =
    chartData.reduce((sum, d) => sum + Number(d.spending || 0), 0) / chartData.length;

  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 40, right: 30, left: 10, bottom: 30 }}
        >
          {/* Background grid */}
          <CartesianGrid strokeDasharray="3 3" vertical={false} />

          {/* X Axis: formatted dates */}
          <XAxis
            dataKey="label"
            tick={{ fill: '#6B7280', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
          />

          {/* Y Axis: dollar formatting */}
          <YAxis
            tickFormatter={(v) => `$${v}`}
            tick={{ fill: '#6B7280', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
          />

          {/* Hover tooltip */}
          <Tooltip
            formatter={(value) => [`$${value.toFixed(2)}`, 'Spending']}
            labelStyle={{ fontWeight: 'bold' }}
            contentStyle={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
          />

          {/* Manual legend for lines */}
          <Legend
            verticalAlign="top"
            height={36}
            payload={[
              {
                value: 'Budget',
                type: 'line',
                id: 'budgetLine',
                color: '#FF0000',
                strokeDasharray: '3 3',
                strokeWidth: 5.28
              },
              {
                value: 'Avg Spending',
                type: 'line',
                id: 'avgLine',
                color: '#10B981',
                strokeDasharray: '3 3',
                strokeWidth: 5.28
              },
            ]}
          />

          {/* Budget reference line */}
          <ReferenceLine
            y={budget}
            stroke="#FF0000"
            strokeWidth={3.28}
            ifOverflow="extendDomain"
          />

          {/* Average spending reference line */}
          <ReferenceLine
            y={averageSpending}
            stroke="#10B981"  /* Green-500 from Tailwind */
            strokeWidth={8.28}
            strokeDasharray="3 3"
            ifOverflow="extendDomain"
          />

          {/* Spending bars */}
          <Bar
            dataKey="spending"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`bar-${index}`}
                fill={entry.isCurrent ? '#3B82F6' : '#CBD5E1'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BudgetBarChart;
